import { useState } from 'react';
import { VendorSetupMedia } from '@/contracts/vendor-setup';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Upload, X, Image, Video } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Props {
  data: VendorSetupMedia;
  onChange: (data: VendorSetupMedia) => void;
}

export function SetupMediaStep({ data, onChange }: Props) {
  const [uploading, setUploading] = useState<string | null>(null);

  const uploadFile = async (file: File, type: 'logo' | 'hero' | 'gallery') => {
    setUploading(type);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${type}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;
      
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName);
      
      if (type === 'logo') {
        onChange({ ...data, logo_url: urlData.publicUrl });
      } else if (type === 'hero') {
        onChange({ ...data, hero_image_url: urlData.publicUrl });
      } else {
        onChange({ ...data, gallery: [...data.gallery, urlData.publicUrl] });
      }
      
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(null);
    }
  };

  const removeGalleryImage = (index: number) => {
    const newGallery = [...data.gallery];
    newGallery.splice(index, 1);
    onChange({ ...data, gallery: newGallery });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold mb-2">Media & Branding</h2>
        <p className="text-muted-foreground">
          Upload high-quality images that showcase your product. These will appear on your listing page.
        </p>
      </div>

      {/* Logo */}
      <div className="space-y-3">
        <Label>Company Logo</Label>
        <div className="flex items-start gap-4">
          {data.logo_url ? (
            <div className="relative">
              <img 
                src={data.logo_url} 
                alt="Logo" 
                className="w-24 h-24 object-contain rounded-lg border bg-background"
              />
              <Button
                size="icon"
                variant="destructive"
                className="absolute -top-2 -right-2 h-6 w-6"
                onClick={() => onChange({ ...data, logo_url: undefined })}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <label className="w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors">
              <Upload className="h-6 w-6 text-muted-foreground" />
              <span className="text-xs text-muted-foreground mt-1">Upload</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], 'logo')}
                disabled={uploading === 'logo'}
              />
            </label>
          )}
          <p className="text-sm text-muted-foreground">
            Square format, 512x512px minimum. PNG or JPG.
          </p>
        </div>
      </div>

      {/* Hero Image */}
      <div className="space-y-3">
        <Label>Hero Image</Label>
        <div className="space-y-3">
          {data.hero_image_url ? (
            <div className="relative inline-block">
              <img 
                src={data.hero_image_url} 
                alt="Hero" 
                className="max-w-md h-48 object-cover rounded-lg border"
              />
              <Button
                size="icon"
                variant="destructive"
                className="absolute top-2 right-2 h-6 w-6"
                onClick={() => onChange({ ...data, hero_image_url: undefined })}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <label className="block max-w-md h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors">
              <Image className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground mt-2">Upload hero image</span>
              <span className="text-xs text-muted-foreground">1200x630px recommended</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], 'hero')}
                disabled={uploading === 'hero'}
              />
            </label>
          )}
        </div>
      </div>

      {/* Gallery */}
      <div className="space-y-3">
        <Label>Product Screenshots / Gallery</Label>
        <div className="flex flex-wrap gap-3">
          {data.gallery.map((url, index) => (
            <div key={index} className="relative">
              <img 
                src={url} 
                alt={`Screenshot ${index + 1}`} 
                className="w-32 h-24 object-cover rounded-lg border"
              />
              <Button
                size="icon"
                variant="destructive"
                className="absolute -top-2 -right-2 h-6 w-6"
                onClick={() => removeGalleryImage(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {data.gallery.length < 6 && (
            <label className="w-32 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground mt-1">Add image</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], 'gallery')}
                disabled={uploading === 'gallery'}
              />
            </label>
          )}
        </div>
        <p className="text-sm text-muted-foreground">Up to 6 screenshots. Show your product in action!</p>
      </div>

      {/* Demo Video */}
      <div className="space-y-3">
        <Label htmlFor="demo_video">Demo Video URL (optional)</Label>
        <div className="flex items-center gap-3">
          <Video className="h-5 w-5 text-muted-foreground" />
          <Input
            id="demo_video"
            placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
            value={data.demo_video_url || ''}
            onChange={(e) => onChange({ ...data, demo_video_url: e.target.value || undefined })}
          />
        </div>
        <p className="text-sm text-muted-foreground">YouTube or Vimeo link to a product demo</p>
      </div>
    </div>
  );
}
